/**
 * Style / detail catalogue and variant helpers for the AI generator (v2).
 *
 * Plain dependency-free JS (no React, no DOM) so it can be unit tested with
 * plain `node` — see app/ai-generator/__tests__/variants.test.js — the same
 * reason checkoutAutoopen.js lives on its own.
 *
 * Backend contract (svghmi_server routes/ai-generator.js, 2026-10-12):
 *   GET  /styles   → { styles:[{id,label,description}], default,
 *                      details:['schematic','simple','medium'],
 *                      default_detail:{ flat:'medium', 'hp-hmi':'schematic', … },
 *                      concept_pipeline_enabled? (NOT sent yet — see below) }
 *   GET  /job/:id  → a parked concept job: status 'awaiting_pick' with
 *                      concepts:[{ idx, round, url, auto_score }], expires_at;
 *                    the planned v2 shape: status 'variants_ready' with
 *                      variants:[{ idx, preview_url, style, detail, design?, label? }],
 *                      variants_expires_at (unix seconds)
 * Both spellings are read. Every field is optional here: an older backend
 * simply yields an empty catalogue / no variants, and the UI falls back to the
 * one-widget flow.
 */

// Equipment ("parts") styles, in the backend's catalogue order. Used when
// /styles has not answered or predates `default_detail`; labels and
// descriptions are ours unless the backend sends its own for the same id.
// The labels are always ours (owner-approved UX copy, 2026-10-12): the backend
// ids stay, the backend's own labels are only used for an id we do not know.
export const EQUIPMENT_STYLES = Object.freeze([
  { id: 'flat', label: 'Flat', description: 'Solid fills, clean outlines, no shading.' },
  { id: 'hp-hmi', label: 'ISA-101 (High Performance)', description: 'ISA-101 greys: calm screen, colour only for state and alarms.' },
  { id: 'convex', label: '3D / Convex', description: 'Soft gradients give every part volume.' },
  { id: 'line-art', label: 'P&ID line art', description: 'Outlines of one even width, no fills.' },
  { id: 'dark', label: 'Dark screen', description: 'Dark-screen greys with light outlines.' },
]);

// Pseudo-style: send nothing and let the backend pick from the prompt.
export const AUTO_STYLE = 'auto';

// Pseudo-style: the Icon card. Sent as style 'icon', which forces the icon
// path on the backend (wantsIconPath); never a parts style.
export const ICON_STYLE = 'icon';

export const DETAIL_LEVELS = Object.freeze([
  { id: 'schematic', label: 'P&ID symbol', description: 'A process symbol, as on a P&ID.' },
  { id: 'simple', label: 'Simple', description: 'The equipment, few parts.' },
  { id: 'medium', label: 'Detailed', description: 'The equipment with its main details.' },
]);

const DEFAULT_DETAIL = 'medium';

// Stored / prompt-path style ids → the selectable equipment style they render as.
// Mirrors STYLE_ID_ALIASES and PARTS_STYLE_ALIASES on the backend.
const STYLE_TO_EQUIPMENT = Object.freeze({
  'flat-hmi': 'flat',
  'high-performance-hmi': 'hp-hmi',
  'glass-industrial': 'convex',
  'schematic-pid': 'line-art',
  'neon-dark': 'dark',
  icon: 'flat',
});

const prettify = (id) => String(id || '')
  .replace(/[-_]+/g, ' ')
  .replace(/^\w/, (c) => c.toUpperCase());

/**
 * Build the picker catalogue from a GET /styles response (or null).
 *
 * The detail picker only makes sense on the concept (equipment) path, which is
 * behind an admin flag on the backend (ai_config.generator_concept_pipeline,
 * default OFF). /styles lists the details whatever the flag says, so the
 * picker stays hidden until the backend also reports
 * `concept_pipeline_enabled: true` — a field it does not send yet.
 *
 * @param {object|null} resp
 * @returns {{ styles: {id,label,description,defaultDetail}[],
 *             details: {id,label,description}[], supportsDetail: boolean }}
 */
export function buildStyleCatalog(resp) {
  const r = resp && typeof resp === 'object' ? resp : {};
  const backendDefaults = r.default_detail && typeof r.default_detail === 'object'
    ? r.default_detail
    : null;
  const backendStyles = Array.isArray(r.styles) ? r.styles : [];
  const backendById = new Map(
    backendStyles.filter((s) => s && typeof s.id === 'string').map((s) => [s.id, s]),
  );
  const localById = new Map(EQUIPMENT_STYLES.map((s) => [s.id, s]));

  const ids = backendDefaults ? Object.keys(backendDefaults) : EQUIPMENT_STYLES.map((s) => s.id);
  const styles = ids.map((id) => {
    const own = backendById.get(id) || {};
    const local = localById.get(id) || {};
    // hp-hmi defaults to a schematic symbol, every other style to medium.
    let def = id === 'hp-hmi' ? 'schematic' : DEFAULT_DETAIL;
    if (backendDefaults && typeof backendDefaults[id] === 'string') def = backendDefaults[id];
    return {
      id,
      label: local.label || own.label || prettify(id),
      description: own.description || local.description || '',
      defaultDetail: def,
    };
  });

  // Details only when the backend says it understands them AND that the
  // concept path is on; otherwise the picker stays hidden and nothing extra is
  // sent.
  const detailIds = Array.isArray(r.details) ? r.details.filter((d) => typeof d === 'string') : [];
  const localDetail = new Map(DETAIL_LEVELS.map((d) => [d.id, d]));
  const details = detailIds.map((id) => ({
    id,
    label: (localDetail.get(id) || {}).label || prettify(id),
    description: (localDetail.get(id) || {}).description || '',
  }));

  const conceptOn = r.concept_pipeline_enabled === true;
  return {
    styles, details, supportsDetail: conceptOn && details.length > 0 && !!backendDefaults,
  };
}

/** The selectable equipment style a stored style id renders as, or null. */
export function equipmentStyleOf(styleId) {
  if (typeof styleId !== 'string' || !styleId) return null;
  if (EQUIPMENT_STYLES.some((s) => s.id === styleId)) return styleId;
  if (STYLE_TO_EQUIPMENT[styleId]) return STYLE_TO_EQUIPMENT[styleId];
  // Concrete convex presets are stored as e.g. 'convex-matte'.
  if (styleId.startsWith('convex')) return 'convex';
  return null;
}

/** Label of a style id (any of its spellings) from the catalogue. */
export function styleLabel(catalog, styleId) {
  const id = equipmentStyleOf(styleId) || styleId;
  const found = (catalog?.styles || []).find((s) => s.id === id)
    || EQUIPMENT_STYLES.find((s) => s.id === id);
  return found ? found.label : prettify(styleId);
}

/** Label of a detail level. */
export function detailLabel(detailId) {
  const found = DETAIL_LEVELS.find((d) => d.id === detailId);
  return found ? found.label : prettify(detailId);
}

/**
 * What POST /generate should carry for the picker state: nothing for "auto",
 * the style id otherwise, plus the detail when the backend understands it.
 */
export function generateStyleParams(catalog, styleId, detailId) {
  if (!styleId || styleId === AUTO_STYLE) return {};
  const out = { style: styleId };
  const style = (catalog?.styles || []).find((s) => s.id === styleId);
  if (catalog?.supportsDetail && style
    && (catalog.details || []).some((d) => d.id === detailId)) {
    out.detail = detailId;
  }
  return out;
}

/**
 * The variants of a job response, normalised. Accepts both the top-level
 * `variants` array (contract) and one nested under `result`; a boolean
 * `variants` (the older concept flag) yields an empty list.
 *
 * @returns {{idx:number, previewUrl:string|null, previewSvg:string|null,
 *            style:string|null, detail:string|null, label:string|null,
 *            design:string|null}[]}
 */
export function getVariants(job) {
  if (!job || typeof job !== 'object') return [];
  let raw = null;
  if (Array.isArray(job.variants)) raw = job.variants;
  else if (job.result && Array.isArray(job.result.variants)) raw = job.result.variants;
  else if (Array.isArray(job.concepts)) {
    // The concept pipeline's sketches (status awaiting_pick): { idx, url }.
    raw = job.concepts.map((c) => (c && typeof c === 'object'
      ? { ...c, preview_url: c.preview_url || c.url } : c));
  }
  if (!raw) return [];
  return raw
    .filter((v) => v && Number.isInteger(Number(v.idx)))
    .map((v) => ({
      idx: Number(v.idx),
      previewUrl: typeof v.preview_url === 'string' ? v.preview_url : null,
      previewSvg: typeof v.rendered_svg === 'string' ? v.rendered_svg : null,
      style: typeof v.style === 'string' ? v.style : null,
      detail: typeof v.detail === 'string' ? v.detail : null,
      label: typeof v.label === 'string' && v.label.trim() ? v.label.trim() : null,
      design: typeof v.design === 'string' ? v.design : null,
    }));
}

// Job statuses that mean "waiting for the user to pick one". 'awaiting_pick'
// is what the concept pipeline parks in today; 'variants_ready' is the v2 name.
export const PICK_STATUSES = Object.freeze(['variants_ready', 'awaiting_pick']);

export const isVariantsReady = (job) => !!job && PICK_STATUSES.includes(job.status);

/** When the variants of a job expire (unix seconds), or null when unknown. */
export function variantsExpiresAt(job) {
  if (!job) return null;
  const t = Number(job.variants_expires_at ?? job.expires_at);
  return Number.isFinite(t) && t > 0 ? t : null;
}

/** A variants_ready job the user can still choose from. */
export function isChoosable(job, nowSeconds = Date.now() / 1000) {
  if (!isVariantsReady(job)) return false;
  const exp = variantsExpiresAt(job);
  return exp === null || exp > nowSeconds;
}

/** "A", "B", "C"… for the i-th tile (falls back to a number past Z). */
export const variantLetter = (i) => (
  i >= 0 && i < 26 ? String.fromCharCode(65 + i) : String(i + 1)
);

/** Caption for one variant tile: "Variant A", "Variant B"… */
export function variantCaption(_catalog, _variant, i) {
  return `Variant ${variantLetter(i)}`;
}

/** Secondary line for a tile: its own label, else "Style · Detail", or null. */
export function variantDetails(catalog, variant) {
  if (variant.label) return variant.label;
  const parts = [];
  if (variant.style) parts.push(styleLabel(catalog, variant.style));
  if (variant.detail) parts.push(detailLabel(variant.detail));
  return parts.length ? parts.join(' · ') : null;
}

/**
 * Can "Show more variants" be offered for this completed result? Only when the
 * backend says so outright (`more_available === true`, GET /job/:id since
 * 2026-10-12, from moreProblem()) — hidden by default, so an older backend or
 * a payload without the field never shows a button that cannot work. Never on
 * a sample, an icon or the concept path (that one has its own restyle
 * switcher), and not once the extras were used (`more_used`).
 */
export function canAskMoreVariants(result) {
  if (!result || result.is_sample) return false;
  if (result.pipeline === 'concept' || result.pipeline === 'icon') return false;
  if (result.more_used === true) return false;
  return result.more_available === true;
}

/** Restyle applies to completed concept-pipeline widgets only. */
export const canRestyle = (result) => !!result && !result.is_sample && result.pipeline === 'concept';
